// time-proposal-modal.component.ts
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { meetingSchedulerServices } from 'src/app/services/meeting-scheduler.service';
import { CalendarComponent } from 'src/app/shared/calendar/calendar.component';
import { MeetingDetailsComponent } from './meeting-details/meeting-details.component';
import { firstValueFrom } from 'rxjs';
import { MeetingShareComponent } from './meeting-share/meeting-share.component';
import { MeetingSchedulerComponent } from 'src/app/shared/meeting-scheduler/meeting-scheduler.component';
import { GeneralMeetingSchedulerComponent } from './general-meeting-scheduler/general-meeting-scheduler.component';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { environment } from 'src/environments/environment';
import { userDataService } from 'src/app/services/userDataService';

@Component({
  selector: 'app-time-proposal-modal',
  standalone: true,
  imports: [MaterialModule, CommonModule, CalendarComponent, FormsModule, MeetingDetailsComponent, 
    MeetingShareComponent, GeneralMeetingSchedulerComponent],
  templateUrl: './time-proposal-modal.component.html',
  styleUrl: './time-proposal-modal.component.scss'
})
export class TimeProposalModalComponent implements OnInit {

  showAutoWidth = output<boolean>();
  
  hourSegments = 4;
  meetingDuration: string = "30";
  allScheduledMeetings: any[] = [];
  currentStep: number = 1;
  
  // Meeting details
  meetingName: string = '';
  meetingLocation: string = '';
  meetingDescription: string = '';
  companyName: string = '';
  videoCallOption: string = 'Make it a Google Meet meeting';
  
  // Fields for the invitee
  prefillNameEmail: boolean = false;
  requireName: boolean = true;
  requireEmail: boolean = true;
  requirePhone: boolean = false;
  meetingId: string = "";
  type: string = "general";
  generalMeetingData: any;

  editMode: boolean = false;
  editRecord: any;
  meetingForm: any;
  
  // Store latest meeting data to handle navigation
  latestMeetingData: any = null;

  // Add property for lead data
  currentLead: any = null;

  defaultEventProps : any = {
    type: {
      id: 1,
      created_at: "2025-02-18T20:58:55.328Z",
      updated_at: "2025-02-18T20:58:55.328Z",
      company_id: 1,
      name: "fdsf",
      description: null,
      sequence: 1,
      active: true,
      external_defaultType: false,
      sync_google_calendar: false,
      icon: "cloud_download",
      color: null,
      isTask: false
    },
    color: {
      primary: "#0d68c5",
      secondary: "#0d68c5"
    },
    meta: {
      tmpEvent: true,
      isModal: false,
      meetingScheduler: true
    },
    draggable: true,
    resizable: {
      beforeStart: false,
      afterEnd: false
    }
  };

  currentTimezone: string = '';
  timezoneOffset: string = '';
  isLoading: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<TimeProposalModalComponent>, 
    private meetingScheduler: meetingSchedulerServices,
    private showsnack: NotificationsService,
    private cdr: ChangeDetectorRef,
    private userDataService:userDataService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if(data){
      this.type = data.type;
      this.editMode = data.editMode;
      this.editRecord = data.data;
      
      // Store lead data if provided
      if(data.lead) {
        this.currentLead = data.lead;
      }
    }
    this.initializeTimezone();
  }
  
  @ViewChild(CalendarComponent) calendar!: CalendarComponent;
  @ViewChild(MeetingDetailsComponent) meetingDetails!: MeetingDetailsComponent;
  @ViewChild(GeneralMeetingSchedulerComponent) generalMeetingScheduler!: GeneralMeetingSchedulerComponent;

  async ngOnInit() {
    if(this.editMode){
      await this.loadMeetingData();
    }
  }

  async loadMeetingData() {
    try {
      
      const id = this.editRecord.id || this.meetingId;
      if (!id) return;
      const response = await firstValueFrom(this.meetingScheduler.getOne(id));
      // Store the data for when navigating back
      this.latestMeetingData = response;
      
      this.generalMeetingData = {
        duration: response.duration,
        startDate: response.startDate,
        endDate: response.endDate,
        availability: response.availability,
        bufferSettings: {
          bufferTimeBefore: response.bufferTimeBefore,
          bufferTimeAfter: response.bufferTimeAfter,
          shortestNotice: response.shortestNotice,
          bookingFuturePeriod: response.bookingFuturePeriod,
          timeSlotIncrement: response.timeSlotIncrement
        }
      }

      this.meetingDuration = response.duration;

      this.allScheduledMeetings = response.proposedTimes.map((pt) => ({
        id: pt.id,                  // updated id from proposedTime
        title: response.title || "Proposed Time",       // updated title from meeting
        start: new Date(pt.startTime),        // updated start from proposedTime.startTime
        end: new Date(pt.endTime),            // updated end from proposedTime.endTime
        ...this.defaultEventProps        // rest of the default properties
      }));

      // Determine the mode based on the createLead flag
      const createLeadMode = response.createLead ? 'lead-and-meeting' : 'meeting-only';

      this.meetingForm = {
        // Meeting details
        meetingName: response.title,
        meetingLocation: response.location,
        videoCallLink: response.videoCallLink,
        meetingDescription: response.description,
        companyName: response.companyName,
        fields: response.fields,
        createLeadMode: createLeadMode, // Set the mode based on the createLead flag
        createLead: response.createLead, // Also pass the raw createLead flag
        footerText: response.footerText,
        type_id: response.type_id,
        activityNote: response.activityNote,
        // Add source data for edit mode
        source_id: response.source_id || null,
        sourceId: response.source_id || null // Also set sourceId for form compatibility
      }
      
      // Force update of the UI
      this.cdr.detectChanges();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading meeting data:', error);
      this.isLoading = false;
    }
  }

  private initializeTimezone(): void {
    // Get user's current timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.currentTimezone = timezone;
    
    // Get timezone offset in format +/-HH:MM
    const now = new Date();
    const offsetMinutes = now.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
    const offsetMins = Math.abs(offsetMinutes % 60);
    const offsetSign = offsetMinutes <= 0 ? '+' : '-';
    
    this.timezoneOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
    // The offset is stored in a standardized format that is timezone-aware
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  goBack(): void {
    if (this.currentStep > 1) {
      const previousStep = this.currentStep - 1;
      this.currentStep = previousStep;
      this.showAutoWidth.emit(this.currentStep === 3 ? false : true);
      
      // If going back to step 2 from step 3, reload the meeting data to reflect saved changes
      if (previousStep === 2 && this.latestMeetingData) {
        this.isLoading = true;
        setTimeout(() => {
          this.loadMeetingData();
        }, 100); // Short delay to ensure the component is ready
      }
    }
  }

  goToNextStep(): void {
    if (this.currentStep < 3) {
      this.currentStep++;
    } else {
      this.finishSetup();
    }
    this.showAutoWidth.emit(this.currentStep === 3 ? false : true)
  }

// Updated continueToDetails method in TimeProposalModalComponent
continueToDetails(): void {
  // Get selected times from calendar if needed
  this.allScheduledMeetings = this.calendar?.allScheduledMeetings || [];
  this.meetingDuration = this.calendar?.meetingDuration || this.meetingDuration;
  this.generalMeetingData = this.generalMeetingScheduler?.saveAvailability();
  this.currentStep = 2;
  this.showAutoWidth.emit(this.currentStep === 3 ? false : true);
  
  // Prefill meeting details if lead data is available and we don't already have meeting data
  if (this.currentLead && !this.meetingForm) {
    // Format the full name correctly
    const fullName = this.currentLead.fullName || 
                     (this.currentLead.name && this.currentLead.lastName ? 
                      `${this.currentLead.name} ${this.currentLead.lastName}` : 
                      (this.currentLead.name || ''));
    
    // Create default field structure for name, email, and phone
    const defaultFields = [
      {
        fieldName: "Full name",
        fieldType: "text",
        isRequired: true,
        isDefault: true,
        showInScheduler: true,
        prefillValue: fullName, // Set full name here
        order: 0
      },
      {
        fieldName: "Email",
        fieldType: "email",
        isRequired: true,
        isDefault: true,
        showInScheduler: true,
        prefillValue: this.currentLead.email || "",
        order: 1
      },
      {
        fieldName: "Phone",
        fieldType: "tel",
        isRequired: true,
        isDefault: true,
        showInScheduler: true,
        prefillValue: this.currentLead.prefix && this.currentLead.phoneNumber ? 
                      this.currentLead.prefix + this.currentLead.phoneNumber : 
                      "",
        order: 2
      }
    ];

    // Create initial meeting form data
    this.meetingForm = {
      meetingName: 'Meeting',
      meetingLocation: '',
      videoCallOption: 'google-meet',
      meetingDescription: '',
      companyName: this.currentLead?.Company?.name,
      fields: defaultFields,
      createLeadMode: 'meeting-only', // Don't create another lead
      createLead: false,
      footerText: '',
      type_id: '', 
      activityNote: '',
      source_id: this.currentLead.source_id || null,
      sourceId: this.currentLead.source_id || null
    };
    
    // Force UI update
    this.cdr.detectChanges();
  }
}

  hasSelectedTimes(){
    let availabilities : any = []
    if(this.generalMeetingScheduler){
      availabilities  = this.generalMeetingScheduler?.saveAvailability().availability;
    }
    const hasAtLeastOneTimeSlot = availabilities?.some(day => day.timeSlots && day.timeSlots.length > 0);
    return (this.allScheduledMeetings.length || this.calendar?.allScheduledMeetings.length || hasAtLeastOneTimeSlot) ? false : true;
  }

  async continueToShare() {
    // Save meeting details
    const isInValid = this.meetingDetails?.meetingForm?.value?.fields?.find(row => !row.fieldName);
    if(isInValid){
      this.showsnack.showClientError("Please fill in all required fields");
      return;
    }
  
    const createLead = this.meetingDetails.meetingForm.value.createLeadMode === 'lead-and-meeting';
  
    // Map UI proposedTimes to Prisma ProposedTime format
    const proposedTimesMapped = this.allScheduledMeetings.map(meeting => ({
        startTime: meeting.start, // Start time from UI event
        endTime: meeting.end, // End time from UI event
        isSelected: false // Default to false, can be updated later
    }));
  
    // Preserve ALL field properties without filtering and include order
    const allFields = this.meetingDetails.meetingForm.value.fields.map((field, index) => {
      // Create a clean copy of each field with all properties preserved
      return {
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        isRequired: field.isRequired, // Preserve isRequired as is
        isDefault: field.isDefault || false,
        isLeadField: field.isLeadField || false,
        showInScheduler: field.showInScheduler, // Preserve showInScheduler as is
        options: field.options || '',
        prefillValue: field.prefillValue || '',
        order: field.order !== undefined ? field.order : index, // Preserve or assign order
        key: field.key || null // Preserve field key for mapping
      };
    });
  
    // Sort fields by order to maintain proper sequence
    allFields.sort((a, b) => (a.order || 0) - (b.order || 0));
  
    // Filter fields based on the selected mode for display only
    const displayFields = allFields.filter(field => {
      // In lead-and-meeting mode:
      if (createLead) {
        // Include all lead fields with showInScheduler=true
        if (field.isLeadField) {
          return field.showInScheduler;
        }
        // Include all custom fields
        if (!field.isDefault && !field.isLeadField) {
          return true;
        }
        // Exclude default fields
        return false;
      } 
      // In meeting-only mode:
      else {
        // Include all default fields and custom fields, exclude lead fields
        return !field.isLeadField;
      }
    });
    
    this.meetingForm = {
      // Meeting details
      meetingName: this.meetingDetails.meetingForm.value.meetingName,
      meetingLocation: this.meetingDetails.meetingForm.value.meetingLocation,
      videoCallLink: this.meetingDetails.meetingForm.value.videoCallOption,
      meetingDescription: this.meetingDetails.meetingForm.value.meetingDescription,
      companyName: this.meetingDetails.meetingForm.value.companyName,
      footerText: this.meetingDetails.meetingForm.value.footerText || null,
      type_id: this.meetingDetails.meetingForm.value.type_id,
      activityNote: this.meetingDetails.meetingForm.value.activityNote,
      createLeadMode: this.meetingDetails.meetingForm.value.createLeadMode,
      sourceId: this.meetingDetails.meetingForm.value.sourceId // Include source
    }
  
    const user = await this.userDataService.getUserData();
    
    // Map Meeting Form to Prisma MeetingScheduler model
    const data = {
      title: this.meetingDetails.meetingForm.value.meetingName,
      description: this.meetingDetails.meetingForm.value.meetingDescription,
      location: this.meetingDetails.meetingForm.value.meetingLocation,
      videoCallLink: this.meetingDetails.meetingForm.value.videoCallOption,
      companyName: this.meetingDetails.meetingForm.value.companyName,
      note: this.meetingDetails.meetingForm.value.footerText || null,
      status: 'PENDING', // Default value
      calendarType: this.meetingDetails.meetingForm.value.videoCallOption === 'google-meet' ? 'GOOGLE' : null, 
      active: true, // Default to true
      proposedTimes: proposedTimesMapped, // Include the mapped proposed times
      duration: this.type !== 'general'? Number(this.meetingDuration) : Number(this.generalMeetingData?.duration),
      type_id: this.meetingDetails.meetingForm.value.type_id,
      activityNote: this.meetingDetails.meetingForm.value.activityNote,
      footerText: this.meetingDetails.meetingForm.value.footerText,
      
      // Add new lead creation flag
      createLead: createLead,
      // Send ALL fields with their properties preserved and ordered
      fields: allFields,
      
      organizerId: user?.id,
      startDate: this.generalMeetingData?.startDate,
      endDate: this.generalMeetingData?.endDate,
      availability: this.generalMeetingData?.availability || [],
  
      bufferTimeBefore: Number(this.generalMeetingData?.bufferSettings?.bufferTimeBefore),
      bufferTimeAfter: Number(this.generalMeetingData?.bufferSettings?.bufferTimeAfter),
      shortestNotice: Number(this.generalMeetingData?.bufferSettings?.shortestNotice),
      bookingFuturePeriod: Number(this.generalMeetingData?.bufferSettings?.bookingFuturePeriod),
      timeSlotIncrement: Number(this.generalMeetingData?.bufferSettings?.timeSlotIncrement),
      timezone: this.currentTimezone,
      timezoneOffset: this.timezoneOffset,
      
      // Add source field
      source_id: this.meetingDetails.meetingForm.value.sourceId || null,
      
      // Pass the filtered display fields to use in the UI
      displayFields: displayFields,
      
      // REMOVE the lead_id field - it's not in the Prisma model
      // lead_id: leadId  <-- This line is causing the error
    };
    
    // Send the formatted data to the API
    let response: any;
    try {
      if(this.editMode){
        response = await firstValueFrom(this.meetingScheduler.put(data, this.editRecord.id));
      } else {
        response = await firstValueFrom(this.meetingScheduler.post(data));
      }
      // Store the latest response data to use when navigating back
      this.latestMeetingData = response;
      this.meetingId = response.id;
      
      this.currentStep = 3;
      this.showAutoWidth.emit(this.currentStep === 3 ? false : true);
      this.editMode = true;
      this.editRecord = response;
    } catch (error) {
      console.error('Error saving meeting:', error);
      this.showsnack.showClientError("Error saving meeting data. Please try again.");
    }
  }

  sendMeetingLink(type: 'sms' | 'email'): void {
    // Get the meeting link
    const meetingLink = `${environment.uiUrl}/meeting-schedule/${this.meetingId}`;
    
    // Create the message text
    const messageText = `Here's my calendar link to schedule: ${meetingLink}`;
    
    // Create a subject for email (only used for email type)
    const subject = `Meeting scheduling link: ${this.meetingForm?.meetingName || 'Meeting'}`;
    
    // Close the dialog and return data with action type, message, and subject
    this.dialogRef.close({ 
      action: 'send',
      type: type,
      meetingId: this.meetingId,
      messageText: messageText,
      subject: subject
    });
  }

  finishSetup(): void {
    // Complete the process
    this.dialogRef.close({ action: 'done', meetingId: this.meetingId });
  }
}